// TODO - lots of selectors

import { ResponseError } from 'superagent';
import {
  getEmbedConfiguration,
  getLockSpec,
} from 'redux/middleware/api/embed-configuration-service/actions';
import {
  getMediaSegmentDataByRevisionId,
  getVideoLayoutTimelineByRevisionId,
} from 'redux/middleware/api/media-upload-service/actions';
import { fetchRecordings } from 'redux/middleware/api/recording-service/actions';
import {
  analyzeText,
  getKeywordsForRecordingVersion,
  getVideoClipIdsFromConfigs,
  loadVideosForConfigs,
} from 'redux/modules/common/actions';
import {
  mediaSegmentRevisionIdSelector,
  recordingSelector,
  videoSelector,
} from 'redux/modules/entities';
import { getVideoLayoutTimelineRevisionIdSelector } from 'redux/modules/entities/selectors/videoLayoutTimeline';
import { Dispatch, GetState } from 'redux/types';
import { IEmbedConfig, IEmbedConfiguration } from 'types';
import { getCaptionsMediaSource } from 'utils/embed/captions';
import { VideoLayoutTimelineIdData } from 'utils/embed/layout';
import { MediaSegmentIdData } from 'utils/embed/media-segment';
import {
  embedConfigDataSelector,
  embedConfigSelector,
  mediaSegmentInfoIdsSelector,
  videoLayoutTimelineIdsSelector,
} from '../selectors';

const fetchAllRecordings = (recordingIds: number[]) => async (
  dispatch: Dispatch,
  getState: GetState,
) => {
  if (recordingIds.length === 0) {
    return;
  }

  await dispatch(fetchRecordings(recordingIds));

  const isAllFetched = recordingIds
    .map(id => recordingSelector(getState(), id))
    .every(Boolean);

  if (!isAllFetched) {
    const error = new Error('Audio asset not found') as ResponseError;
    error.status = 404;
    throw error;
  }
};

const loadRecordings = () => async (dispatch: Dispatch, getState: GetState) => {
  const config = embedConfigDataSelector(getState()) as IEmbedConfig;
  const audioInfo = config?.audioInfo ?? [];

  const recordingsToFetch = audioInfo.reduce((acc, { recordingId }) => {
    if (!recordingSelector(getState(), recordingId)) {
      acc.push(recordingId);
    }

    return acc;
  }, [] as number[]);

  await dispatch(fetchAllRecordings(recordingsToFetch));
};

const loadVideos = () => async (dispatch: Dispatch, getState: GetState) => {
  const config = embedConfigDataSelector(getState()) as IEmbedConfig;

  await dispatch(loadVideosForConfigs(config, { includeVariations: true }));

  // Strict guard for the advanced-editor load path: if a videoClip references
  // an upload that couldn't be fetched, surface a 404 so the editor can show
  // an asset-not-found error rather than silently rendering with stale data.
  const referencedIds = getVideoClipIdsFromConfigs(config);
  const isAllFetched = referencedIds.every(id =>
    Boolean(videoSelector(getState(), id)),
  );

  if (!isAllFetched) {
    const error = new Error('Video asset not found') as ResponseError;
    error.status = 404;
    throw error;
  }
};

const processCaptionsMediaSource = () => async (
  dispatch: Dispatch,
  getState: GetState,
) => {
  // TODO selector, but there is one, but it's for something different?
  const config = embedConfigSelector(getState()) as IEmbedConfiguration;
  const captionsMediaSource = getCaptionsMediaSource(config);
  if (!captionsMediaSource) return;

  const { id, type } = captionsMediaSource;

  if (type === 'text') {
    await dispatch(analyzeText(id, undefined, false));
  }
  if (type === 'audio') {
    const recording = recordingSelector(getState(), id);
    const { versionId } = recording ?? {};

    if (versionId) {
      await dispatch(getKeywordsForRecordingVersion(versionId, false));
    }
  }
};

const getMediaSegmentInfoIfNecessary = ({
  id,
  revisionId,
}: MediaSegmentIdData) => async (dispatch: Dispatch, getState: GetState) => {
  const currentMediaSegmentRevisionId = mediaSegmentRevisionIdSelector(
    getState(),
    id,
  );

  if (
    !currentMediaSegmentRevisionId ||
    revisionId !== currentMediaSegmentRevisionId
  ) {
    await dispatch(getMediaSegmentDataByRevisionId({ uuid: id, revisionId }));
  }
};

const loadMediaSegmentInfo = () => async (
  dispatch: Dispatch,
  getState: GetState,
) => {
  const mediaSegmentInfoIds = mediaSegmentInfoIdsSelector(getState());
  await Promise.all(
    mediaSegmentInfoIds.map(id => dispatch(getMediaSegmentInfoIfNecessary(id))),
  );
};

const getVideoLayoutTimelineIfNecessary = ({
  uuid,
  revisionId,
}: VideoLayoutTimelineIdData) => async (
  dispatch: Dispatch,
  getState: GetState,
) => {
  const currentVideoLayoutTimelineRevisionId = getVideoLayoutTimelineRevisionIdSelector(
    uuid,
  )(getState());

  if (
    !currentVideoLayoutTimelineRevisionId ||
    revisionId !== currentVideoLayoutTimelineRevisionId
  ) {
    await dispatch(getVideoLayoutTimelineByRevisionId({ uuid, revisionId }));
  }
};

const loadVideoLayoutData = () => async (
  dispatch: Dispatch,
  getState: GetState,
) => {
  const layoutTimelineIds = videoLayoutTimelineIdsSelector(getState());
  await Promise.all(
    layoutTimelineIds.map(id =>
      dispatch(getVideoLayoutTimelineIfNecessary(id)),
    ),
  );
};

const loadLockSpec = () => async (dispatch: Dispatch, getState: GetState) => {
  const lockSpecUuid = embedConfigDataSelector(getState())?.lockSpec?.uuid;
  if (!lockSpecUuid) return;
  await dispatch(getLockSpec({ lockSpecId: lockSpecUuid }));
};

const loadEmbedConfigurationDependencies = () => async (dispatch: Dispatch) => {
  try {
    await dispatch(loadRecordings());
    await dispatch(loadVideos());
    await dispatch(processCaptionsMediaSource());
    await dispatch(loadMediaSegmentInfo());
    await dispatch(loadVideoLayoutData());
    await dispatch(loadLockSpec());
  } catch (err) {
    if (err.status !== 404) throw err;
  }
};

export const loadEmbedConfiguration = (wid: string) => async (
  dispatch: Dispatch,
) => {
  await dispatch(getEmbedConfiguration(wid));
  await dispatch(loadEmbedConfigurationDependencies());
};
